import type { CaptionCue, TrackInfo } from './types';

export interface PlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isSeeking: boolean;
  volume: number;
  isMuted: boolean;
  bufferedEnd: number;
  isBuffering: boolean;
  captionTracks: TrackInfo[];
  selectedTrackIdx: number;
  captions: CaptionCue[];
  activeCueText: string;
  activeCueEnd: number;
  heatmapVisible: boolean;
  isLooping: boolean;
  isFullscreen: boolean;
  controlsVisible: boolean;
  activeSourceIndex: number;
  actionsMenuOpen: boolean;
  waveformVisible: boolean;
  captionPickerOpen: boolean;
  timecodePopoverOpen: boolean;
  sidePanelOpen: boolean;
  sidePanelHasElement: boolean;
  sidePanelWidth: number;
  isReady: boolean;
  playbackError: { hasError: boolean; message: string };
  autoplay: boolean;
  showMarkers: boolean;
  showHeatmapsOnHover: boolean;
  pauseWhileTyping: boolean;
  autoCaptions: boolean;
}

export type PlayerAction =
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TOGGLE_PLAY' }
  | { type: 'TIME_UPDATE'; currentTime: number }
  | { type: 'DURATION_SET'; duration: number }
  | { type: 'SEEK_START' }
  | { type: 'SEEK_END'; time: number }
  | { type: 'SET_VOLUME'; volume: number }
  | { type: 'TOGGLE_MUTE' }
  | { type: 'MUTE' }
  | { type: 'UNMUTE' }
  | { type: 'BUFFER_UPDATE'; bufferedEnd: number }
  | { type: 'SET_BUFFERING'; isBuffering: boolean }
  | { type: 'SET_CAPTIONS'; captions: CaptionCue[] }
  | { type: 'SET_CAPTION_TRACKS'; tracks: TrackInfo[] }
  | { type: 'SELECT_TRACK'; index: number }
  | { type: 'SET_ACTIVE_CUE'; text: string; end?: number }
  | { type: 'TOGGLE_HEATMAP' }
  | { type: 'TOGGLE_LOOP' }
  | { type: 'ENTER_FULLSCREEN' }
  | { type: 'EXIT_FULLSCREEN' }
  | { type: 'SET_PLAYBACK_RATE'; rate: number }
  | { type: 'SWITCH_SOURCE'; index: number }
  | { type: 'TOGGLE_WAVEFORM' }
  | { type: 'SET_WAVEFORM_VISIBLE'; visible: boolean }
  | { type: 'TOGGLE_ACTIONS_MENU' }
  | { type: 'TOGGLE_CAPTION_PICKER' }
  | { type: 'TOGGLE_TIMECODE_POPOVER' }
  | { type: 'CLOSE_TIMECODE_POPOVER' }
  | { type: 'CLOSE_ALL_MENUS' }
  | { type: 'SET_CONTROLS_VISIBLE'; visible: boolean }
  | { type: 'TOGGLE_SIDE_PANEL' }
  | { type: 'SET_SIDE_PANEL'; open: boolean }
  | { type: 'SET_SIDE_PANEL_HAS_ELEMENT'; hasElement: boolean }
  | { type: 'SET_SIDE_PANEL_WIDTH'; width: number }
  | { type: 'MEDIA_READY' }
  | { type: 'MEDIA_ENDED' }
  | { type: 'SET_PLAYBACK_ERROR'; message: string }
  | { type: 'CLEAR_PLAYBACK_ERROR' }
  | { type: 'TOGGLE_AUTOPLAY' }
  | { type: 'TOGGLE_SHOW_MARKERS' }
  | { type: 'TOGGLE_SHOW_HEATMAPS_ON_HOVER' }
  | { type: 'TOGGLE_PAUSE_WHILE_TYPING' }
  | { type: 'TOGGLE_AUTO_CAPTIONS' };

function getSavedSetting(key: string, defaultValue: boolean): boolean {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return defaultValue;
    return val !== 'false';
  } catch {
    return defaultValue;
  }
}

function getSavedLoop(): boolean {
  try {
    return localStorage.getItem('player.loop') === 'true';
  } catch {
    return false;
  }
}

function getSavedVolume(): number {
  try {
    const saved = localStorage.getItem('acornvolume');
    if (saved !== null) {
      const val = parseFloat(saved);
      if (!isNaN(val) && val >= 0 && val <= 1) return val;
    }
  } catch {
    // localStorage not available
  }
  return 1.0;
}

export function createInitialState(): PlayerState {
  return {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1.0,
    isSeeking: false,
    volume: getSavedVolume(),
    isMuted: false,
    bufferedEnd: 0,
    isBuffering: true,
    captionTracks: [],
    selectedTrackIdx: -1,
    captions: [],
    activeCueText: '',
    activeCueEnd: 0,
    heatmapVisible: false,
    isLooping: getSavedLoop(),
    isFullscreen: false,
    controlsVisible: true,
    activeSourceIndex: 0,
    actionsMenuOpen: false,
    waveformVisible: false,
    captionPickerOpen: false,
    timecodePopoverOpen: false,
    sidePanelOpen: false,
    sidePanelHasElement: false,
    sidePanelWidth: 300,
    isReady: false,
    playbackError: { hasError: false, message: '' },
    autoplay: getSavedSetting('player.settings.autoplay', true),
    showMarkers: getSavedSetting('player.settings.showMarkers', true),
    showHeatmapsOnHover: getSavedSetting('player.settings.showHeatmapsOnHover', true),
    pauseWhileTyping: getSavedSetting('player.settings.pauseWhileTyping', true),
    autoCaptions: getSavedSetting('player.settings.autoCaptions', true),
  };
}

export function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case 'PLAY':
      return { ...state, isPlaying: true };

    case 'PAUSE':
      return { ...state, isPlaying: false };

    case 'TOGGLE_PLAY':
      return { ...state, isPlaying: !state.isPlaying };

    case 'TIME_UPDATE':
      if (state.isSeeking || state.currentTime === action.currentTime) return state;
      return { ...state, currentTime: action.currentTime };

    case 'DURATION_SET':
      return { ...state, duration: action.duration };

    case 'SEEK_START':
      return { ...state, isSeeking: true };

    case 'SEEK_END':
      return { ...state, isSeeking: false, currentTime: action.time };

    case 'SET_VOLUME':
      // When muted the media element volume is 0, and the browser fires
      // volumechange which dispatches SET_VOLUME with 0.  Don't overwrite
      // the remembered volume — it's needed when unmuting.
      if (state.isMuted && action.volume === 0) return state;
      return { ...state, volume: action.volume };

    case 'TOGGLE_MUTE':
      return { ...state, isMuted: !state.isMuted };

    case 'MUTE':
      return { ...state, isMuted: true };

    case 'UNMUTE':
      return { ...state, isMuted: false };

    case 'BUFFER_UPDATE':
      return { ...state, bufferedEnd: action.bufferedEnd };

    case 'SET_BUFFERING':
      return { ...state, isBuffering: action.isBuffering };

    case 'SET_CAPTIONS':
      return { ...state, captions: action.captions };

    case 'SET_CAPTION_TRACKS':
      return { ...state, captionTracks: action.tracks };

    case 'SELECT_TRACK':
      return { ...state, selectedTrackIdx: action.index, captionPickerOpen: false };

    case 'SET_ACTIVE_CUE':
      if (state.activeCueText === action.text && state.activeCueEnd === (action.end ?? 0)) return state;
      return { ...state, activeCueText: action.text, activeCueEnd: action.end ?? 0 };

    case 'TOGGLE_HEATMAP':
      return { ...state, heatmapVisible: !state.heatmapVisible };

    case 'TOGGLE_WAVEFORM':
      return { ...state, waveformVisible: !state.waveformVisible, actionsMenuOpen: false };

    case 'SET_WAVEFORM_VISIBLE':
      return { ...state, waveformVisible: action.visible };

    case 'TOGGLE_LOOP':
      return { ...state, isLooping: !state.isLooping, actionsMenuOpen: false };

    case 'ENTER_FULLSCREEN':
      return { ...state, isFullscreen: true };

    case 'EXIT_FULLSCREEN':
      return { ...state, isFullscreen: false };

    case 'SET_PLAYBACK_RATE':
      return { ...state, playbackRate: action.rate, actionsMenuOpen: false };

    case 'SWITCH_SOURCE':
      return { ...state, activeSourceIndex: action.index, actionsMenuOpen: false, playbackError: { hasError: false, message: '' } };

    case 'TOGGLE_ACTIONS_MENU':
      return { ...state, actionsMenuOpen: !state.actionsMenuOpen, captionPickerOpen: false, timecodePopoverOpen: false };

    case 'TOGGLE_CAPTION_PICKER':
      return { ...state, captionPickerOpen: !state.captionPickerOpen, actionsMenuOpen: false, timecodePopoverOpen: false };

    case 'TOGGLE_TIMECODE_POPOVER':
      return { ...state, timecodePopoverOpen: !state.timecodePopoverOpen, actionsMenuOpen: false, captionPickerOpen: false };

    case 'CLOSE_TIMECODE_POPOVER':
      return { ...state, timecodePopoverOpen: false };

    case 'CLOSE_ALL_MENUS':
      return { ...state, actionsMenuOpen: false, captionPickerOpen: false, timecodePopoverOpen: false };

    case 'SET_CONTROLS_VISIBLE':
      return { ...state, controlsVisible: action.visible };

    case 'TOGGLE_SIDE_PANEL':
      return { ...state, sidePanelOpen: !state.sidePanelOpen };

    case 'SET_SIDE_PANEL':
      return { ...state, sidePanelOpen: action.open };

    case 'SET_SIDE_PANEL_HAS_ELEMENT':
      return { ...state, sidePanelHasElement: action.hasElement };

    case 'SET_SIDE_PANEL_WIDTH':
      return { ...state, sidePanelWidth: action.width };

    case 'MEDIA_READY':
      return { ...state, isReady: true, isBuffering: false };

    case 'MEDIA_ENDED':
      return { ...state, isPlaying: false };

    case 'SET_PLAYBACK_ERROR':
      return { ...state, playbackError: { hasError: true, message: action.message } };

    case 'CLEAR_PLAYBACK_ERROR':
      return { ...state, playbackError: { hasError: false, message: '' } };

    case 'TOGGLE_AUTOPLAY':
      return { ...state, autoplay: !state.autoplay };

    case 'TOGGLE_SHOW_MARKERS':
      return { ...state, showMarkers: !state.showMarkers };

    case 'TOGGLE_SHOW_HEATMAPS_ON_HOVER':
      return { ...state, showHeatmapsOnHover: !state.showHeatmapsOnHover };

    case 'TOGGLE_PAUSE_WHILE_TYPING':
      return { ...state, pauseWhileTyping: !state.pauseWhileTyping };

    case 'TOGGLE_AUTO_CAPTIONS':
      return { ...state, autoCaptions: !state.autoCaptions };

    default:
      return state;
  }
}
